import PageHeader from "../components/ds/PageHeader";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiCheck, FiLoader, FiRotateCcw, FiX } from "react-icons/fi";
import {
  acceptRefundRequest,
  getRefundRequests,
  rejectRefundRequest,
} from "../api/mintSpace";
import { useTranslation } from "../context/TranslationContext";
import ConfirmModal from "../components/ConfirmModal";
import ReasonModal from "../components/ReasonModal";
import UserProfileLink from "../components/UserProfileLink";

/**
 * Buyers asking this business for their money back — the owner's to decide.
 *
 * Accepting refunds the purchase in full through Keepz: the money goes back to how the
 * buyer paid, comes out of the owner's balance, and the buyer's access ends. Rejecting
 * can say why; the buyer is told either way.
 *
 * @see mintly-back/src/services/refundService
 */

const money = (amount, currency = "GEL") =>
  `${currency === "GEL" ? "₾" : `${currency} `}${Number(amount || 0).toFixed(2)}`;
const day = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
};

const TONE = {
  pending: "bg-status-warning-bg text-status-warning",
  refunded: "bg-status-success-bg text-status-success",
  rejected: "bg-status-danger-bg text-status-danger",
};

export default function MintSpaceRefundRequestsPage({ isDark, setToast, mintSpaceId, onPendingCountChange }) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  /** { request, action: "accept" | "reject" } while a decision is being confirmed. */
  const [deciding, setDeciding] = useState(null);
  const [busy, setBusy] = useState(false);
  // Read from a ref, so a parent passing a fresh callback each render doesn't refetch.
  const onCountRef = useRef(onPendingCountChange);
  onCountRef.current = onPendingCountChange;

  const load = useCallback(async () => {
    if (!mintSpaceId) return;
    try {
      const res = await getRefundRequests(mintSpaceId);
      setRequests(res?.data || []);
      onCountRef.current?.(res?.pendingCount ?? 0);
    } catch (err) {
      setToast?.(err?.message || t("refunds.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [mintSpaceId, setToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (reason = "") => {
    if (!deciding) return;
    const { request, action } = deciding;
    setBusy(true);
    try {
      if (action === "accept") await acceptRefundRequest(mintSpaceId, request.id);
      else await rejectRefundRequest(mintSpaceId, request.id, reason);
      setToast?.(t(action === "accept" ? "refunds.refunded" : "refunds.rejected"));
      setDeciding(null);
    } catch (err) {
      setToast?.(err?.message || t("refunds.decideFailed"));
      if (err?.code === "already_decided") setDeciding(null);
    } finally {
      setBusy(false);
      load();
    }
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const card = "border-separator-default bg-bg-raised";
  const quote = isDark ? "border-white/10 bg-black/20 text-slate-200" : "border-slate-200 bg-slate-50 text-slate-700";
  const target = deciding?.request;

  return (
    <div className="flex w-full flex-col gap-space-24 p-space-8 lg:p-0">
      <PageHeader title={t("refunds.title")} subtitle={t("refunds.subtitle")} />

      {loading ? (
        <div className="flex justify-center py-12">
          <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
        </div>
      ) : requests.length === 0 ? (
        <div
          className={`flex flex-col items-center rounded-radius-md border border-dashed px-6 py-12 text-center ${
            "border-separator-default"
          }`}
        >
          <FiRotateCcw className={`mb-3 h-7 w-7 ${mutedText}`} />
          <p className={`text-subheadline-semibold ${baseText}`}>{t("refunds.empty")}</p>
          <p className={`mt-1 text-subheadline ${mutedText}`}>{t("refunds.emptyHint")}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {requests.map((r) => (
            <li key={r.id} className={`rounded-radius-md border p-4 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <UserProfileLink
                    user={r.buyer}
                    avatarSize="sm"
                    isDark={isDark}
                    nameClassName={`text-subheadline-semibold ${baseText}`}
                    fallback={t("refunds.unknownBuyer")}
                  />
                  <p className={`mt-1 truncate text-subheadline ${baseText}`}>
                    {r.productName || t("purchases.untitled")}
                    <span className={`font-semibold`}> · {money(r.amount, r.currency)}</span>
                  </p>
                  <p className={`text-caption-1 ${mutedText}`}>
                    {t("refunds.dates", { bought: day(r.purchasedAt), asked: day(r.requestedAt) })}
                  </p>
                </div>
                <span className={`shrink-0 rounded-radius-xs px-2 py-1 text-caption-1-semibold ${TONE[r.status] || TONE.pending}`}>
                  {t(`refunds.status.${r.status}`)}
                </span>
              </div>

              {r.reason && (
                <p className={`mt-3 whitespace-pre-line break-words rounded-radius-sm border px-3 py-2 text-subheadline ${quote}`}>
                  {r.reason}
                </p>
              )}

              {r.status === "pending" ? (
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeciding({ request: r, action: "reject" })}
                    className={`inline-flex items-center gap-1.5 rounded-radius-sm border px-3.5 py-2 text-subheadline-semibold transition hover:border-status-danger hover:text-status-danger ${
                      isDark ? "border-white/10 text-slate-200" : "border-slate-200 text-slate-700"
                    }`}
                  >
                    <FiX className="h-4 w-4" />
                    {t("refunds.reject")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeciding({ request: r, action: "accept" })}
                    className="inline-flex items-center gap-1.5 rounded-radius-sm bg-accent-default px-3.5 py-2 text-subheadline-semibold text-accent-on transition hover:opacity-90"
                  >
                    <FiCheck className="h-4 w-4" />
                    {t("refunds.accept")}
                  </button>
                </div>
              ) : (
                <p className={`mt-3 text-footnote ${mutedText}`}>
                  {r.status === "rejected"
                    ? r.decisionReason
                      ? t("refunds.rejectedOnWithReason", { date: day(r.decidedAt), reason: r.decisionReason })
                      : t("refunds.rejectedOn", { date: day(r.decidedAt) })
                    : t("refunds.refundedOn", { date: day(r.decidedAt) })}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmModal
        isOpen={deciding?.action === "accept"}
        onClose={() => !busy && setDeciding(null)}
        onConfirm={() => decide()}
        title={target ? t("refunds.acceptTitle", { amount: money(target.amount, target.currency) }) : ""}
        message={
          target
            ? t("refunds.acceptMessage", {
                buyer: target.buyer?.businessName || target.buyer?.username || t("refunds.unknownBuyer"),
                product: target.productName || t("purchases.untitled"),
              })
            : ""
        }
        confirmLabel={t("refunds.acceptConfirm")}
        loading={busy}
        isDark={isDark}
      />
      <ReasonModal
        isOpen={deciding?.action === "reject"}
        onClose={() => !busy && setDeciding(null)}
        onConfirm={decide}
        title={t("refunds.rejectTitle")}
        message={t("refunds.rejectMessage")}
        label={t("refunds.rejectReasonLabel")}
        placeholder={t("refunds.rejectReasonPlaceholder")}
        confirmLabel={t("refunds.rejectConfirm")}
        maxLength={500}
        variant="danger"
        loading={busy}
        isDark={isDark}
      />
    </div>
  );
}
